import { Form, Formik, FormikProps } from 'formik';
import * as bd from 'react-basic-design';
import {
    AccessRoleDTO,
    AccessTeamDTO,
    AccessTeamRoleDTO,
    AccessTeamUserDTO,
    useDeleteRoleTeamsApi,
    useSaveTeamRolesApi,
} from '../../api/role-teams-api';
import SvgDelete from '../../assets/icons/Delete';
import SvgSave from '../../assets/icons/Save';
import { notify } from '../../components/basic/notify';
import { T, translate } from '../../components/basic/text';
import { FormikCellToggle, FormikTextField } from '../../components/formik';
import { code } from '../../components/basic/code';
import SvgVisibility from '../../assets/icons/Visibility';
import SvgCheckBox from '../../assets/icons/CheckBox';
import SvgCheckBoxOutlineBlank from '../../assets/icons/CheckBoxOutlineBlank';
import { BasicModal } from '../../components/basic/basic-modal';
import { CSSProperties } from 'react';
type TeamModalProps = {
    team: AccessTeamDTO;
    teamRoles: AccessTeamRoleDTO[];
};

type TeamModalSaveResult = {
    teams: AccessTeamDTO[];
    teamRoles: AccessTeamRoleDTO[];
};

export const ManageTeamModal = ({
    show,
    team,
    roles,
    teamRoles,
    allowEdit,
    onSave,
    onDeleted,
    onHide,
}: {
    show: boolean;
    team?: AccessTeamDTO;
    roles: AccessRoleDTO[];
    teamUsers: AccessTeamUserDTO[];
    teamRoles: AccessTeamRoleDTO[];
    allowEdit: boolean;
    onSave: (result: TeamModalSaveResult) => void;
    onDeleted: VoidFunction;
    onHide: VoidFunction;
}) => {
    const saveRoleTeamsApi = useSaveTeamRolesApi();
    const deleteRoleTeamsApi = useDeleteRoleTeamsApi();
    if (!show) return <></>;

    const onSubmit = (values: TeamModalProps) => {
        const dto = {
            team: values.team,
            teamRoles: values.teamRoles.filter(x => x.accessTeamSerial === values.team.serial),
        };

        saveRoleTeamsApi.post(null, dto, onSave, notify.error);
    };
    const onDelete = (teamSerial: number) =>
        bd.msgbox(
            translate('delete-roleTeam'),
            translate('conform-delete-roleTeam'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteRoleTeamsApi
                            .post({ teamSerial }, null)
                            .then(() => {
                                hide();
                                onDeleted();
                            })
                            .catch(notify.error);
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );

    const getTeamRole = (formik: FormikProps<TeamModalProps>, role: AccessRoleDTO) => {
        return formik.values.teamRoles.find(
            x => x.roleId === role.id && x.accessTeamSerial === formik.values.team.serial
        );
    };

    const renderCheckBox = (formik: FormikProps<TeamModalProps>, role: AccessRoleDTO) => {
        const style: CSSProperties = { fontSize: '1.5rem' };
        const teamRole = getTeamRole(formik, role);
        const permissionLevel = teamRole?.permissionLevel;

        return (
            <div
                className="m-s-1 m-e-3"
                title={permissionLevel === 'maintain' ? translate('readonly') : ''}
            >
                {!teamRole && <SvgCheckBoxOutlineBlank className="text-muted" style={style} />}
                {teamRole && permissionLevel === 'view' && (
                    <SvgVisibility className="text-warning" style={style} />
                )}
                {teamRole && permissionLevel === 'maintain' && (
                    <SvgCheckBox className="text-primary" style={style} />
                )}
            </div>
        );
    };

    function toggleSelected(formik: FormikProps<TeamModalProps>, role: AccessRoleDTO): any {
        if (!allowEdit) return;

        const teamRole = formik.values.teamRoles.find(
            x => x.roleId === role.id && x.accessTeamSerial === formik.values.team.serial
        );
        if (!teamRole || (teamRole && teamRole.permissionLevel === '')) {
            formik.setFieldValue('teamRoles', [
                ...formik.values.teamRoles,
                {
                    roleId: role.id,
                    accessTeamSerial: formik.values.team.serial,
                    permissionLevel: 'view',
                },
            ]);
        } else if (teamRole && teamRole.permissionLevel === 'maintain') {
            formik.setFieldValue(
                'teamRoles',
                formik.values.teamRoles.filter(
                    x => !(x.roleId === role.id && x.accessTeamSerial === formik.values.team.serial)
                )
            );
        } else {
            formik.setFieldValue(
                'teamRoles',
                formik.values.teamRoles.map(_tr =>
                    _tr.roleId === role.id && _tr.accessTeamSerial === formik.values.team.serial
                        ? { ..._tr, permissionLevel: 'maintain' }
                        : _tr
                )
            );
        }
    }

    const initialValues = {
        team: team || {
            serial: 0,
            title: '',
            isActive: true,
        },
        teamRoles,
    };

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
            {formik => (
                <BasicModal
                    show={show}
                    size="lg"
                    onHide={onHide}
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <div>
                                <T>{team ? 'edit-accessTeamRole' : 'new-accessTeamRole'}</T>
                                <FormikCellToggle
                                    disabled={!allowEdit}
                                    title={formik.values.team.isActive ? 'active' : 'disabled'}
                                    inputMaxWidth="11rem"
                                    name="team.isActive"
                                />
                            </div>
                            <bd.Flex align="center" gap={2}>
                                <bd.Button
                                    disabled={!formik.isValid || !allowEdit}
                                    variant="contained"
                                    onClick={formik.submitForm}
                                    className="min-w-80"
                                >
                                    <SvgSave />
                                    <T>save</T>
                                </bd.Button>
                                {!!team && (
                                    <bd.Button
                                        disabled={!allowEdit}
                                        variant="outline"
                                        type="button"
                                        onClick={() => onDelete(team.serial)}
                                        color="secondary"
                                    >
                                        <SvgDelete className="size-sm" />
                                        <T>delete</T>
                                    </bd.Button>
                                )}
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex align="start" vertical>
                            <FormikTextField
                                disabled={!allowEdit}
                                label="role-team-title"
                                className="w-100"
                                name="team.title"
                                handleEnter
                                type="text"
                            />
                        </bd.Flex>
                        <bd.Flex gap={2}>
                            <T as="div" className="pt-2 text-secondary-text">
                                roles
                            </T>
                        </bd.Flex>
                        <bd.List compact small variant="button" className="py-0">
                            {roles.map((role, i) => (
                                <bd.ListItem
                                    key={i}
                                    variant="menu"
                                    primary={
                                        <bd.Flex>
                                            {renderCheckBox(formik, role)}
                                            <T>{role.title}</T>
                                            <span className="small opacity-50 m-s-3">
                                                ({code.isProductionEnv() ? null : role.id})
                                            </span>
                                        </bd.Flex>
                                    }
                                    onClick={() => allowEdit && toggleSelected(formik, role)}
                                    disabled={!allowEdit}
                                />
                            ))}
                        </bd.List>
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
};
